import { CARRIER_KINDS, type CarrierKind } from '@/lib/api/carrier-connections';
import royalMailCdLogo from '@/assets/carriers/royalmail-cd.svg?url';
import royalMailObaLogo from '@/assets/carriers/royalmail-oba.svg?url';
import dpdLogo from '@/assets/carriers/dpd.svg?url';
import dhlLogo from '@/assets/carriers/dhl.svg?url';
import evriLogo from '@/assets/carriers/evri.svg?url';
import yodelLogo from '@/assets/carriers/yodel.svg?url';
import fedexLogo from '@/assets/carriers/fedex.svg?url';
import upsLogo from '@/assets/carriers/ups.svg?url';

/**
 * Display metadata for each carrier — what the merchant sees in
 * Step 4's card grid and on the future `/app/settings/carriers` page.
 * The data lives next to the wizard rather than in `lib/api` because
 * it's purely presentational (logo URL, marketing one-liner).
 *
 * Logo SVGs live under `apps/web/src/assets/carriers/` and are
 * imported with Vite's `?url` query so they're emitted as static
 * assets and served from the SPA bundle, not inlined.
 *
 * Adding a new carrier card is two lines: a CARRIER_KINDS entry in
 * `lib/api/carrier-connections.ts` (UI catalogue) and a row in this
 * map. Making it interactive (i.e. moving from "Coming soon" to
 * "Connect") requires the SDK package + DB enum + dialog wiring per
 * ADR 0015.
 */
export interface CarrierCatalogueEntry {
  readonly displayName: string;
  readonly tagline: string;
  /**
   * Required — every carrier in the catalogue must ship with a logo
   * asset under `apps/web/src/assets/carriers/`. The type enforces it
   * so we never quietly render an `<img src={undefined}>` if a
   * future carrier entry forgets to wire its asset.
   */
  readonly logoUrl: string;
}

export const CARRIER_CATALOGUE: Record<CarrierKind, CarrierCatalogueEntry> = {
  royalmail_cd: {
    displayName: 'Royal Mail Click & Drop',
    tagline: 'Connect your existing C&D account using an API token.',
    logoUrl: royalMailCdLogo,
  },
  royalmail_oba: {
    displayName: 'Royal Mail OBA',
    tagline: 'Direct integration with your Online Business Account.',
    logoUrl: royalMailObaLogo,
  },
  dpd: {
    displayName: 'DPD',
    tagline: 'Generate DPD labels for UK + international.',
    logoUrl: dpdLogo,
  },
  dhl: {
    displayName: 'DHL',
    tagline: 'Express and Parcel services worldwide.',
    logoUrl: dhlLogo,
  },
  evri: {
    displayName: 'Evri',
    tagline: 'UK-focused parcel network (formerly Hermes).',
    logoUrl: evriLogo,
  },
  yodel: {
    displayName: 'Yodel',
    tagline: 'UK home delivery for B2C parcels.',
    logoUrl: yodelLogo,
  },
  fedex: {
    displayName: 'FedEx',
    tagline: 'Express international shipping.',
    logoUrl: fedexLogo,
  },
  ups: {
    displayName: 'UPS',
    tagline: 'Standard and Express UK + international.',
    logoUrl: upsLogo,
  },
};

/**
 * Stable ordering for the card grid. Royal Mail variants first (the
 * MVP carriers), then the rest alphabetically. Driven from
 * `CARRIER_KINDS` so a new entry there appears on the grid without
 * touching this file.
 */
export const ORDERED_CARRIER_KINDS: readonly CarrierKind[] = CARRIER_KINDS;
